import React from "react";

export default function FinancialInclusionSection() {
  return (
    <section className="w-full bg-white text-gray-800 overflow-hidden" id="financial-inclusion">
      {/* Hero */}
      <div className="px-6 py-16 text-center bg-gradient-to-b from-purple-900 to-purple-700 text-white">
        <h1 className="text-3xl font-bold md:text-4xl">Bringing Every African Into the Financial Future</h1>
        <p className="mt-4 text-base md:text-lg max-w-2xl mx-auto opacity-90">
          MastaKash makes saving, borrowing, lending, and money movement accessible for every individual,
          group, and community in Africa.
        </p>
      </div>

      {/* Problem Section */}
      <div className="px-6 py-12 max-w-5xl mx-auto">
        <h2 className="text-2xl font-semibold text-purple-900 mb-4">The Financial Inclusion Problem</h2>
        <p className="text-gray-700 leading-relaxed mb-4">
          Despite progress, access to formal finance in Africa remains limited. Over <strong>350 million Africans</strong> are unbanked or
          underbanked, relying heavily on cash—unsafe, expensive, and impossible to track. Women and youth
          face even higher barriers due to cost, distance, and limited trust in traditional institutions.
        </p>
        <p className="text-gray-700 leading-relaxed">
          Informal systems like chamas and table banking remain largely manual and disconnected from the digital
          economy, preventing millions from accessing credit or building financial security.
        </p>
      </div>

      {/* MastaKash Solution */}
      <div className="px-6 py-12 bg-gray-50">
        <h2 className="text-2xl font-semibold text-purple-900 mb-4">MastaKash: Digital Infrastructure for Inclusive Finance</h2>
        <p className="text-gray-700 leading-relaxed mb-6 max-w-4xl">
          MastaKash digitizes and automates the community financial systems that Africans rely on every day.
          We make it possible for anyone to save securely, borrow responsibly, manage group activities, and
          build trusted financial histories.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {["Payments & Money Transfers","Digital Table Banking","Smart Merry-Go-Rounds","Savings & Credit Infrastructure","Personal Finance Tools","Sukuma Overdraft (Coming Soon)"].map((item, idx) => (
            <div key={idx} className="p-4 bg-white rounded-xl shadow-md border border-purple-100">
              <p className="font-medium text-purple-800">{item}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Who We Serve */}
      <div className="px-6 py-12 max-w-5xl mx-auto">
        <h2 className="text-2xl font-semibold text-purple-900 mb-4">Who We Serve</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {[
            {title: "Individuals", text: "Financial tools that support saving, borrowing, and daily money management."},
            {title: "Groups & Chamas", text: "Digital governance, transparent contributions, loans, and automated cycles."},
            {title: "Agents", text: "Earn income while powering last-mile financial access in your community."},
          ].map((card, idx) => (
            <div key={idx} className="bg-white p-5 rounded-xl shadow border border-gray-200">
              <h3 className="font-semibold text-purple-800 mb-2">{card.title}</h3>
              <p className="text-gray-700 text-sm leading-relaxed">{card.text}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Social Impact */}
      <div className="px-6 py-12 bg-purple-50">
        <h2 className="text-2xl font-semibold text-purple-900 mb-4">The Social Impact</h2>
        <ul className="space-y-4 text-gray-700 max-w-4xl">
          <li><strong>Empowering Women & Youth:</strong> Digital tools strengthen credit access and business growth.</li>
          <li><strong>Reducing Poverty:</strong> Digital savings and credit systems increase financial stability.</li>
          <li><strong>Strengthening Community Governance:</strong> Transparent records eliminate disputes and strengthen trust.</li>
          <li><strong>Building Financial Identity:</strong> Digital transaction history unlocks opportunities with SACCOs and banks.</li>
        </ul>
      </div>

      {/* SDG Alignment */}
      <div className="px-6 py-12 max-w-5xl mx-auto">
        <h2 className="text-2xl font-semibold text-purple-900 mb-4">Aligned With Global & National Goals</h2>
        <p className="text-gray-700 leading-relaxed mb-4">
          MastaKash aligns with UN Sustainable Development Goals (SDGs) including No Poverty, Gender Equality,
          Decent Work & Economic Growth, and Reduced Inequalities. We also support Kenya and Africa's financial
          inclusion strategies driven by regulators and development partners.
        </p>
      </div>
    </section>
  );
}